import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw, Zap } from "lucide-react";
import { Badge } from "../../../../components/ui/Badge";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import { RelativeTime } from "../../../../components/ui/RelativeTime";
import { Select } from "../../../../components/ui/Select";
import { Table, TBody, TD, TH, THead, TR } from "../../../../components/ui/Table";
import { TableSkeleton } from "../../../../components/ui/TableSkeleton";
import { getAiActions, isEndpointMissing } from "./aiActionsApi";
import { ChipRadioGroup, SECONDARY_BTN_CLS } from "./agentSections/primitives";

// Auditoría de lo que la IA hizo en los casos: qué capacidad pidió, si quedó
// esperando la confirmación del contacto, si se ejecutó o si falló. Es la
// contracara de la tarjeta de cuenta de servicio — habilita permisos y acá se
// ve qué se hizo con ellos.
//
// Solo lectura: no hay acciones sobre una fila. La conversación se muestra por
// id porque la bandeja (/admin/helpdesk) todavía no acepta un deep-link por
// conversación (?phone= es el único que soporta; ver HelpdeskPage).

// Ventana de días. El backend recibe `days` como entero. La etiqueta viaja
// junto al valor: agregar un 90 sin rotularlo mostraría "Últimos 30 días".
const DAY_OPTIONS = [
  { value: 7, label: "Últimos 7 días" },
  { value: 30, label: "Últimos 30 días" },
];

// Tope de filas de la vista. El contrato del endpoint no trae un total, así que
// cuando la respuesta llega justo en el tope se dice explícitamente que puede
// haber más: una auditoría truncada en silencio se lee como "esto fue todo".
const ACTIONS_LIMIT = 50;

// Estado → tono del chip. Un estado desconocido cae a `neutral` y se pinta con
// el texto crudo: preferimos mostrar un valor sin traducir antes que inventar
// una etiqueta que el backend no dijo.
const STATUS_TONES = {
  pending: "warning",
  confirming: "warning",
  awaiting_confirmation: "warning",
  confirmed: "info",
  executed: "success",
  ok: "success",
  success: "success",
  failed: "error",
  error: "error",
  rejected: "neutral",
  cancelled: "neutral",
  canceled: "neutral",
  expired: "muted",
};

const STATUS_LABELS = {
  pending: "Pendiente",
  confirming: "Esperando confirmación",
  awaiting_confirmation: "Esperando confirmación",
  confirmed: "Confirmada",
  executed: "Ejecutada",
  ok: "Ejecutada",
  success: "Ejecutada",
  failed: "Falló",
  error: "Falló",
  rejected: "Rechazada",
  cancelled: "Cancelada",
  canceled: "Cancelada",
  expired: "Expirada",
};

/**
 * Resumen legible de los `args` de una acción (`{ package_id: "abc", lat: -33 }`
 * → `package_id=abc, lat=-33`). JSON crudo en una celda es ilegible y rompe el
 * ancho de la tabla; los valores anidados sí van como JSON.
 * @param {unknown} args
 * @param {number} [max=90] - Largo máximo antes de truncar con elipsis.
 * @returns {string}
 */
export function summarizeArgs(args, max = 90) {
  if (args === null || args === undefined || args === "") return "";
  let text;
  if (typeof args === "string") {
    text = args;
  } else if (typeof args !== "object") {
    text = String(args);
  } else {
    text = Object.entries(args)
      .filter(([, value]) => value !== null && value !== undefined && value !== "")
      .map(([key, value]) => `${key}=${typeof value === "object" ? JSON.stringify(value) : String(value)}`)
      .join(", ");
  }
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/**
 * Etiqueta de una capacidad: la del catálogo si el backend la expone, si no el
 * id crudo (mejor un identificador técnico que una celda vacía).
 * @param {object} catalog
 * @param {string} id
 * @returns {string}
 */
export function capabilityLabel(catalog, id) {
  if (!id) return "";
  const found = (catalog?.capabilities || []).find((cap) => cap?.id === id);
  return found?.label || id;
}

export default function AiActionsTab({ agents = [], catalog }) {
  const { t } = useTranslation();

  const [days, setDays] = useState(7);
  const [agentId, setAgentId] = useState("");
  // loading → consultando; ready → hay respuesta (aunque venga vacía);
  // missing → la ruta no está desplegada (404/405); error → falló de verdad.
  const [state, setState] = useState("loading");
  const [actions, setActions] = useState([]);
  const [error, setError] = useState(null);

  // Descarta respuestas de un filtro viejo: cambiar de agente mientras la
  // consulta anterior sigue en vuelo no puede pintar la tabla equivocada.
  const requestIdRef = useRef(0);
  // StrictMode monta → limpia → monta sobre el MISMO fiber: el flag se re-fija
  // dentro del efecto, no solo al declarar el ref.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // `t` cambia de identidad al cambiar el idioma: si entrara a las deps del
  // load, traducir la pantalla volvería a consultar el endpoint (y a parpadear
  // la tabla) sin que cambie ningún filtro. El mensaje de error se traduce con
  // la instancia vigente al momento del fallo.
  const tRef = useRef(t);
  tRef.current = t;

  const load = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const isCurrent = () => aliveRef.current && requestIdRef.current === requestId;

    setState("loading");
    setError(null);
    try {
      const res = await getAiActions({ days, agentId, limit: ACTIONS_LIMIT });
      if (!isCurrent()) return;
      setActions(Array.isArray(res?.actions) ? res.actions : []);
      setState("ready");
    } catch (err) {
      if (!isCurrent()) return;
      if (isEndpointMissing(err)) {
        setState("missing");
      } else {
        setError(err?.message || tRef.current("No se pudieron cargar las acciones."));
        setState("error");
      }
    }
  }, [agentId, days]);

  useEffect(() => {
    load();
  }, [load]);

  const agentName = useCallback(
    (id) => {
      if (!id) return null;
      return agents.find((agent) => agent.id === id)?.name || null;
    },
    [agents],
  );

  const dayOptions = DAY_OPTIONS.map(({ value, label }) => ({ value, label: t(label) }));
  const agentOptions = agents.map((agent) => ({ value: agent.id, label: agent.name }));

  return (
    <section className="space-y-4" aria-label={t("Acciones de IA")}>
      {/* Sin endpoint no hay nada que filtrar: ofrecer filtros que solo pueden
          volver a fallar es ruido. */}
      {state !== "missing" && (
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg">{t("Periodo")}</span>
            <ChipRadioGroup
              value={days}
              options={dayOptions}
              onChange={setDays}
              ariaLabel={t("Periodo")}
            />
          </div>
          <div className="w-full sm:w-64">
            <Select
              label={t("Agente")}
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              placeholder={t("Todos los agentes")}
              options={agentOptions}
            />
          </div>
        </div>
      )}

      {state === "loading" && <TableSkeleton columns={8} rows={5} />}

      {state === "missing" && (
        <PendingBackendNotice>
          {t("La auditoría de acciones todavía no está disponible.")}
        </PendingBackendNotice>
      )}

      {state === "error" && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2"
          role="alert"
        >
          <span className="flex items-center gap-1.5 text-xs text-status-error min-w-0">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            {error}
          </span>
          <button type="button" onClick={load} className={SECONDARY_BTN_CLS}>
            <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
            {t("Reintentar")}
          </button>
        </div>
      )}

      {state === "ready" && actions.length === 0 && (
        <EmptyState
          icon={<Zap className="w-6 h-6" aria-hidden="true" />}
          title={t("No hay acciones de IA en este periodo")}
          description={t(
            "Acá se registra lo que la IA ejecuta en los casos: asignar repartidor, crear incidencias y las demás capacidades que habilites por agente.",
          )}
        />
      )}

      {state === "ready" && actions.length > 0 && (
        <>
          <Table>
            <THead>
              <TR>
                <TH>{t("Fecha")}</TH>
                <TH>{t("Agente")}</TH>
                <TH>{t("Capacidad")}</TH>
                <TH>{t("Argumentos")}</TH>
                <TH>{t("Estado")}</TH>
                <TH>{t("Actor")}</TH>
                <TH>{t("Error")}</TH>
                <TH>{t("Conversación")}</TH>
              </TR>
            </THead>
            <TBody>
              {actions.map((action) => {
                const statusKey = String(action.status || "").toLowerCase();
                const statusLabel = STATUS_LABELS[statusKey];
                const argsText = summarizeArgs(action.args);
                // El tooltip lleva más texto que la celda: si ambos cortaran
                // igual, el valor largo no sería legible en ningún lado.
                const argsFull = summarizeArgs(action.args, 500);
                const name = agentName(action.agent_id);
                return (
                  <TR key={action.id}>
                    <TD className="whitespace-nowrap text-fg-muted">
                      <RelativeTime
                        value={action.executed_at || action.confirmed_at || action.requested_at}
                        t={t}
                        live
                      />
                    </TD>
                    <TD className="whitespace-nowrap">
                      {name || action.agent_id || "—"}
                    </TD>
                    <TD className="whitespace-nowrap">
                      {capabilityLabel(catalog, action.capability) || "—"}
                    </TD>
                    <TD>
                      {argsText ? (
                        <span className="font-mono text-[12px] md:text-[11px] break-all" title={argsFull}>
                          {argsText}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TD>
                    <TD className="whitespace-nowrap">
                      <Badge tone={STATUS_TONES[statusKey] || "neutral"}>
                        {statusLabel ? t(statusLabel) : action.status || t("Desconocido")}
                      </Badge>
                    </TD>
                    <TD className="whitespace-nowrap text-fg-muted">{action.actor || "—"}</TD>
                    <TD>
                      {action.error ? (
                        <span className="text-status-error break-words">{action.error}</span>
                      ) : (
                        "—"
                      )}
                    </TD>
                    {/* Sin link: la bandeja no acepta ?conversation=. El id es
                        lo único que permite cruzar la fila con el caso. */}
                    <TD className="whitespace-nowrap">
                      {action.conversation_id ? (
                        <span className="font-mono text-[12px] md:text-[11px] text-fg-muted">
                          {action.conversation_id}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
          {/* Mismo criterio que la lista de conversaciones: singular explícito
              y replace manual, sin depender de la pluralización de i18next. */}
          <p className="text-[12px] md:text-[11px] text-fg-muted">
            {actions.length === 1
              ? t("1 acción")
              : t("{{count}} acciones").replace("{{count}}", actions.length)}
            {actions.length >= ACTIONS_LIMIT && (
              <>
                <span aria-hidden="true"> · </span>
                {t("Se muestran hasta {{n}}, las más recientes.").replace(
                  "{{n}}",
                  ACTIONS_LIMIT,
                )}
              </>
            )}
          </p>
        </>
      )}
    </section>
  );
}
